import React, { ReactNode } from "react"
import { CookiesProvider } from "react-cookie"
import styled from "styled-components"

import { media, useMedia, useVh } from "@sr/common"

import {
  layoutHeaderHeightMobile,
  layoutHeaderMainHeight,
  layoutHeaderSuppHeight,
  layoutHeaderHeight,
  layoutWidths,
  layoutPadding,
} from "./sizes"
import { layoutZIndexes } from "./zIndexes"
import AppLayoutWidth from "./Width"
import AppCtaBanner from "../CtaBanner"
import AppHeader from "../Header/index"
import AppFooter from "../Footer/index"
import Popup from "../Popup/index"
import { rem } from "polished"
import { IconChevronLeft, IconChevronRight } from "../../Icon"
//@ts-ignore
import { Breadcrumb } from "gatsby-plugin-breadcrumb"
import { cleanBreadcrumbTitle } from "../../../util/cleanBreadcrumbTitle"

export interface AppLayoutProps {
  children: ReactNode
  crumbs?: any
  crumbLabel?: string
  location?: any
}

export const appHeaderHeightMobile = layoutHeaderHeightMobile
export const appHeaderMainHeight = layoutHeaderMainHeight
export const appHeaderSuppHeight = layoutHeaderSuppHeight
export const appHeaderHeight = layoutHeaderHeight

export const appBehindIndex = layoutZIndexes.sub
export const appMainIndex = layoutZIndexes.base
export const appHeaderIndex = layoutZIndexes.mid
export const appSearchIndex = layoutZIndexes.top

const whySmartrentProductPages = [
  "/products/leasing/",
  "/products/maintenance/",
  "/products/management/",
  "/products/operations/",
  "/products/asset-protection/",
  "/products/centralization/",
  "/products/connected-communities/",
  "/products/resident-satisfaction/",
]

const whySmartrentCustomerPages = [
  "/customers/homebuilders/", "/customers/multifamily/", "/customers/single-family-rentals/", "/customers/student-housing/"
]

const AppLayout = ({
  children,
  crumbs,
  crumbLabel,
  location,
}: AppLayoutProps) => {
  const windowVh = useVh()

  const isMobile: boolean = !useMedia(media.midUp)

  /* Clean breadcrumb titles - remove hyphens, capitalize words, etc */
  if (crumbs?.length) {
    crumbs = crumbs.map((crumb: any) => {
      return { ...crumb, crumbLabel: cleanBreadcrumbTitle(crumb.crumbLabel) }
    })
  }

  /* Trim crumb list down to only 1 item for mobile design */
  if (isMobile && crumbs?.length > 1) {
    // Keep only the last two items
    crumbs = crumbs.slice(-2)

    // Update the new first item's crumbLabel
    crumbs[0] = {
      ...crumbs[0],
      crumbLabel: <IconChevronLeft color="var(--color-gray600)" size={14} />,
    }
  }

  // Temp fix for Why SmartRent - If the last crumb is a whySmartrent products page, override it's parent crumbLabel with "Why SmartRent"
  if (crumbs?.length && crumbs[crumbs.length - 1]?.pathname) {
    const lastCrumb = crumbs[crumbs.length - 1]
    const lastCrumbPath = lastCrumb.pathname

    if (lastCrumbPath && whySmartrentProductPages.includes(lastCrumbPath)) {
      const parentCrumb = crumbs[crumbs.length - 2]
      if (parentCrumb) {
        parentCrumb.crumbLabel = "Why SmartRent"
        crumbs[crumbs.length - 2] = {
          ...parentCrumb,
        }
      }
    }
  }

  // Temp fix for Why SmartRent - If the last crumb is a whySmartrent customer page, override it's parent crumbLabel with "Why SmartRent"
  if (crumbs?.length && crumbs[crumbs.length - 1]?.pathname) {
    const lastCrumb = crumbs[crumbs.length - 1]
    const lastCrumbPath = lastCrumb.pathname
    if (lastCrumbPath && whySmartrentCustomerPages.includes(lastCrumbPath)) {
      const parentCrumb = crumbs[crumbs.length - 2]
      if (parentCrumb) {
        parentCrumb.crumbLabel = "Why SmartRent"
        crumbs[crumbs.length - 2] = {
          ...parentCrumb,
        }
      }
    }
  }

  // Override for paginated News pages - If the last crumb is a number (page number), change it to "Page X"
  if (crumbs?.length && crumbs[crumbs.length - 1]?.pathname) {
    const lastCrumb = crumbs[crumbs.length - 1]
    const lastCrumbPath = lastCrumb.pathname
    
    // Check if this is a paginated news page (e.g., /news/2/, /news/3/, etc.)
    if (lastCrumbPath && /^\/news\/\d+\/$/.test(lastCrumbPath)) {
      const pageNumber = lastCrumbPath.split('/').filter(Boolean).pop()
      if (pageNumber && !isNaN(Number(pageNumber))) {
        lastCrumb.crumbLabel = `Page ${pageNumber}`
        crumbs[crumbs.length - 1] = {
          ...lastCrumb,
        }
      }
    }
  }

  const crumbSeparator = isMobile ? null : (
    <IconChevronRight color="var(--color-gray600)" size={14} />
  )

  // If the last crumb is the /products/ page, override it's crumbLabel with "Solutions"
  if(crumbLabel === "Products") {
    crumbLabel = "Solutions"
  }

  return (
    <AppLayoutStyled
      windowVh={windowVh}
      headerIndex={appHeaderIndex}
      mainIndex={appMainIndex}
    >
      <CookiesProvider>
        <AppCtaBanner />
        <Popup />
        <AppHeader />
        <main role="main" className="main">
          {crumbs?.length && location ? (
            <Breadcrumb
              crumbs={crumbs}
              crumbLabel={crumbLabel === "Products" ? "Solutions" : crumbLabel}
              crumbSeparator={crumbSeparator}
              location={location}
            />
          ) : null}
          {children}
        </main>
        <AppFooter />
      </CookiesProvider>
    </AppLayoutStyled>
  )
}

export {
  AppLayout as default,
  AppLayoutWidth,
  layoutWidths,
  layoutPadding,
  layoutZIndexes,
}

interface LayoutSC {
  windowVh: number | null
  headerIndex: number
  mainIndex: number
}

const AppLayoutStyled = styled.div<LayoutSC>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: ${({ windowVh }) =>
    windowVh ? "calc(var(--vh, 1vh) * 100)" : "100vh"};
  .ie11 & {
    display: block;
    min-height: 0;
  }

  .header {
    position: sticky;
    top: 0;
    z-index: ${({ headerIndex }) => headerIndex};
  }

  .main {
    flex: 1 0 auto;
    position: relative;
    z-index: 1;
    z-index: ${({ mainIndex }) => mainIndex};

    .breadcrumb {
      display: flex;
      /* justify-content: center; */
      align-items: center;
      padding: ${rem(24)} ${rem(16)} ${rem(16)};

      @media ${media.midUp} {
        padding-bottom: ${rem(24)};
        padding-left: ${rem(20)};
      }

      .breadcrumb__list {
        display: flex;
        justify-content: center;
        gap: ${rem(8)};

        /* Hide items on mobile except for last */
        .breadcrumb__list__item {
          display: flex;
          justify-content: center;
          align-items: center;

          a {
            display: flex;
            justify-content: center;
            align-items: center;
            color: var(--color-gray600);
            text-decoration: none;
            font-size: ${rem(12)};
            line-height: ${rem(20)};
          }
        }
      }

      .breadcrumb__separator {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
`
